"use client";
import { createLogAdapter } from "@/lib/logger/console-adapter";

import { useEffect, useState } from "react";

const uiLog = createLogAdapter("components/shared/mini-price-chart.tsx");

interface ChartData {
	close: number;
	high: number;
	low: number;
	open: number;
	time: number;
	volume: number;
	date_time: string;
}

interface MiniPriceChartProps {
	tokenId: string;
	width?: number;
	height?: number;
}

export default function MiniPriceChart({
	tokenId,
	width = 60,
	height = 24,
}: MiniPriceChartProps) {
	const [chartData, setChartData] = useState<ChartData[]>([]);
	const [isLoading, setIsLoading] = useState(true);

	useEffect(() => {
		const fetchChartData = async () => {
			try {
				// 计算6小时前的时间
				const now = new Date();
				const sixHoursAgo = new Date(now.getTime() - 6 * 60 * 60 * 1000);
				const fromTime = sixHoursAgo.toISOString();

				const response = await fetch(
					`https://api.odin.fun/v1/token/${tokenId}/tv_feed?resolution=15&from=${encodeURIComponent(fromTime)}`,
				);

				if (response.ok) {
					const data = await response.json();
					setChartData(data || []);
				}
			} catch (error) {
				uiLog.error("Error fetching chart data:", error);
			} finally {
				setIsLoading(false);
			}
		};

		fetchChartData();
	}, [tokenId]);

	if (isLoading || chartData.length === 0) {
		return (
			<div
				style={{ width: `${width}px`, height: `${height}px` }}
				className="bg-gray-200 dark:bg-gray-700 rounded animate-pulse"
			/>
		);
	}

	// 计算价格范围
	const prices = chartData.map((d) => d.close);
	const minPrice = Math.min(...prices);
	const maxPrice = Math.max(...prices);
	const priceRange = maxPrice - minPrice;

	// 计算价格变化判断颜色
	const firstPrice = chartData[0]?.close || 0;
	const lastPrice = chartData[chartData.length - 1]?.close || 0;
	const isPositive = lastPrice >= firstPrice;
	const color = isPositive ? "#10b981" : "#ef4444"; // green-500 or red-500

	// 创建SVG路径
	const createPath = () => {
		if (chartData.length < 2) return "";

		const xStep = width / (chartData.length - 1);
		const points = chartData.map((data, index) => {
			const x = index * xStep;
			const y =
				priceRange > 0
					? height - ((data.close - minPrice) / priceRange) * height
					: height / 2;
			return `${x},${y}`;
		});

		return `M ${points.join(" L ")}`;
	};

	return (
		<svg
			width={width}
			height={height}
			viewBox={`0 0 ${width} ${height}`}
			className="inline-block"
		>
			<path
				d={createPath()}
				fill="none"
				stroke={color}
				strokeWidth="1.5"
				strokeLinecap="round"
				strokeLinejoin="round"
			/>
		</svg>
	);
}
